import { useEffect, useState, type JSX } from 'react'
import { useRouter } from 'next/router'
import { useMediaQuery } from 'usehooks-ts'
import { deviceSizes } from '~/styles/deviceSizes'
import { Button } from '@alto-avios/alto-ui'
import { ContentfulCardLegacy as ContentfulCard } from '@reward-platform/contentful'
import EditSearchResultsForm from '~/components/search-and-results/SearchHotelForm/EditSearchResultsForm'
import {
  gtmSearchSubmit,
  refineSearchSessionStorage,
} from '~/utils/googleTagManager/googleTagManagerHelperFunctions'
import SearchFormProvider from './SearchFormProvider'
import { useDateRangeState, useDestinationState, useHotelSearchForm } from './useHotelSearch'
import { useHotelSearchParams } from './useHotelSearchParams'
import { getRecentSearchesFromLocalStorage } from '../RecentSearches/RecentSearches'

type SearchHotelFormProps = {
  buttonText: string
  trendingDestinations?: ContentfulCard[]
}

function SearchHotelForm({ trendingDestinations, buttonText }: SearchHotelFormProps): JSX.Element {
  const router = useRouter()
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const { data: initialFormData, isResolvingDestination } = useHotelSearchParams()

  const isLargerThanMobile = useMediaQuery(`(min-width: ${deviceSizes.sm})`)
  const { form, errors, handleFormSubmit } = useHotelSearchForm(initialFormData)
  const destination = useDestinationState(form, initialFormData.destination)

  const dates = useDateRangeState(form, [
    initialFormData.startDateTime,
    initialFormData.endDateTime,
  ])

  const onSubmitClick = () => {
    refineSearchSessionStorage(router.pathname)
    gtmSearchSubmit('hotels')
  }

  const recentSearchesData = getRecentSearchesFromLocalStorage()

  const showRecentSearches = !destination.searchString && !!recentSearchesData?.length

  const showPopularDestinations = !!trendingDestinations?.length && !destination.searchString

  return (
    // initialFormData is validated through HotelSearchBrowserParamsSchema.safeParse() inside
    // useHotelSearchParams() before being used in useHotelSearchForm, so no unsanitized browser
    // storage flows into this element.
    // snyk:ignore -- DOM-based XSS: input validated through HotelSearchBrowserParamsSchema.safeParse() inside useHotelSearchParams()
    <SearchFormProvider form={form}>
      <EditSearchResultsForm
        dates={dates}
        submitForm={handleFormSubmit}
        getFieldError={errors.getFieldError}
        getChildAgeError={errors.getChildAgeError}
        getDestinationError={errors.getDestinationError}
        destination={destination}
        showRecentSearches={showRecentSearches}
        {...(showPopularDestinations ? { popularDestinations: trendingDestinations } : {})}
        formButton={
          <Button
            type="submit"
            iconStartProps={isHydrated ? { iconName: 'search', iconPrefix: 'fas' } : undefined}
            size={isHydrated && isLargerThanMobile ? 'lg' : 'md'}
            onClick={onSubmitClick}
            isLoading={form.formState.isSubmitting || isResolvingDestination}
            isDisabled={form.formState.isSubmitting || isResolvingDestination}
          >
            {buttonText}
          </Button>
        }
      />
    </SearchFormProvider>
  )
}

export default SearchHotelForm
